import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useDispatch, useSelector } from 'react-redux';
import { listWorkspaces, deleteWorkspace, getWorkspaceById, setDefaultWorkspace } from '../../../store/auth';
import { setIndicators, setMapIndicators, setWorkspaceLoaded, removeSavedCharts } from 'store/workspaceSlice';
import { setVisualizationType } from "store/visualizationTypeSlice";
import { LuMapPin, LuRefreshCw, LuBarChart2 } from "react-icons/lu";
import { MdOutlineLocalActivity } from "react-icons/md";
import { FiTrash2, FiAlertTriangle } from "react-icons/fi";
import { useNavigate } from 'react-router-dom';
import { FaRegCheckCircle } from "react-icons/fa";
import { CiCircleInfo } from "react-icons/ci";
import { RxCross2 } from "react-icons/rx";
import WorkspaceSidePanel from "components/cmn_workspace/workspace_button";
import WorkspaceButton from "components/pages/workspacebtn/workspacebtn";
import { Prev } from 'react-bootstrap/esm/PageItem';
import { setBarConfig, setPieConfig, setStackedConfig, setMapConfig } from "store/mapConfigSlice";
import { setUlgMapConfig } from "store/ulgmapconfigslice";
import { setUseTemproBarChart, setUseUlgMap } from "store/visualizationTypeSlice";
import { saveCharts, clearSavedCharts } from "../../../store/workspaceSlice";
import { BiBarChartSquare } from "react-icons/bi";

// Enhanced CustomToast Component
const CustomToast = ({ message, type, onClose }) => {
  const getToastConfig = () => {
    switch (type) {
      case 'success':
        return {
          bgColor: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
          textColor: '#155724',
          borderColor: '#c3e6cb',
          icon: <FaRegCheckCircle size={18} style={{ color: '#28a745' }} />
        };
      case 'error':
        return {
          bgColor: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
          textColor: '#721c24',
          borderColor: '#f5c6cb',
          icon: <FiAlertTriangle size={18} style={{ color: '#dc3545' }} />
        };
      case 'info':
        return {
          bgColor: 'linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)',
          textColor: '#0c5460',
          borderColor: '#bee5eb',
          icon: <CiCircleInfo size={18} style={{ color: '#17a2b8' }} />
        };
      case 'warning':
        return {
          bgColor: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
          textColor: '#856404',
          borderColor: '#ffeaa7',
          icon: <FiAlertTriangle size={18} style={{ color: '#ffc107' }} />
        };
      case 'location':
        return {
          bgColor: 'linear-gradient(135deg, #e3f9fd 0%, #b8f4fd 100%)',
          textColor: '#0c4a5a',
          borderColor: '#b8f4fd',
          icon: <LuMapPin size={18} style={{ color: '#31c8eb' }} />
        };
      default:
        return {
          bgColor: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          textColor: '#212529',
          borderColor: '#e9ecef',
          icon: <CiCircleInfo size={18} style={{ color: '#6c757d' }} />
        };
    }
  };

  const config = getToastConfig();

  return (
    <div
      style={{
        background: config.bgColor,
        color: config.textColor,
        padding: '12px 16px',
        borderRadius: '8px',
        fontWeight: 500,
        fontSize: '14px',
        border: `1px solid ${config.borderColor}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: '250px',
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      {config.icon}
      <span style={{ flex: 1 }}>{message}</span>
      {onClose && (
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: config.textColor,
            cursor: 'pointer',
            padding: '2px',
            borderRadius: '3px',
            opacity: 0.7,
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.opacity = 1}
          onMouseLeave={(e) => e.target.style.opacity = 0.7}
        >
          <RxCross2 size={16} />
        </button>
      )}
    </div>
  );
};

// Enhanced CustomConfirm Component - FIXED: Now uses only Blue and White colors
const CustomConfirm = ({ message, onConfirm, onCancel }) => {
  // All confirmation dialogs now use the same blue and white theme
  const config = {
    bgColor: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)',
    borderColor: '#3182ce',
    icon: <CiCircleInfo size={24} style={{ color: '#3182ce' }} />,
    confirmBg: 'linear-gradient(135deg, #3182ce 0%, #2c5282 100%)',
    titleColor: '#2c5282'
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        style={{
          background: config.bgColor,
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          border: `2px solid ${config.borderColor}`,
          animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          {config.icon}
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 'bold',
            color: config.titleColor
          }}>
            Confirm Action
          </h3>
        </div>

        <p style={{
          margin: '0 0 24px 0',
          fontSize: '14px',
          lineHeight: '1.5',
          color: '#4a5568'
        }}>
          {message}
        </p>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)',
              border: '1px solid #cbd5e0',
              color: '#4a5568',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: config.confirmBg,
              border: 'none',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s',
              boxShadow: '0 4px 8px rgba(49, 130, 206, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 12px rgba(49, 130, 206, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 8px rgba(49, 130, 206, 0.3)';
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const Space = () => {
  const { user, isAuthenticated } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Workspace toggle state
  const [showWorkspace, setShowWorkspace] = useState(true);
  const handleVisualize = (selectedIndicators) => {
    navigate('/mapconfig', { state: { selectedIndicators, selectedMapFeatures } });
  };

  const selectedMapFeatures = useSelector(state => state.workspace.selectedMapFeatures);
  const selectedIndicators = useSelector(state => state.workspace.selectedIndicators);

  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [settingDefault, setSettingDefault] = useState(null);
  const [error, setError] = useState('');

  // Alert state (using same pattern as your CustomToast)
  const [alertinfo, setAlertinfo] = useState(null);
  const hasUnsavedChanges = useSelector(state => state.workspace.hasUnsavedChanges);
  const loadedWorkspaceName = useSelector(state => state.workspace.loadedWorkspaceName);

  // Confirm state
  const [confirm, setConfirm] = useState(null);

  // View details state
  const [selectedWorkspaceDetails, setSelectedWorkspaceDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const savedCharts = useSelector((state) => state.workspace.savedCharts);

  // Alert helper function (similar to your existing pattern)
  const showAlert = (message, type = 'info') => {
    setAlertinfo({ message, type });
    // Auto-hide after 4 seconds
    setTimeout(() => setAlertinfo(null), 4000);
  };

  // Confirm helper function - FIXED: Now properly shows confirmation dialog
  const showConfirm = (message, onConfirm) => {
    setConfirm({
      message,
      onConfirm: () => {
        setConfirm(null);
        onConfirm();
      },
      onCancel: () => {
        setConfirm(null);
      }
    });
  };

  // View details helper function
  const handleViewDetails = (workspace) => {
    setSelectedWorkspaceDetails(workspace);
    setShowDetails(true);

    // Scroll to details section after a short delay
    setTimeout(() => {
      const detailsElement = document.getElementById('workspace-details-section');
      if (detailsElement) {
        detailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Close details
  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedWorkspaceDetails(null);
  };

  // Load workspaces from backend
  const loadWorkspaces = async () => {
    if (!isAuthenticated) return;

    setIsRefreshing(true);
    setError('');

    try {
      const response = await listWorkspaces();

      // Handle different response structures
      let workspaces = [];

      if (response && response.data && response.data.workspaces) {
        workspaces = response.data.workspaces;
      } else if (response && response.workspaces) {
        workspaces = response.workspaces;
      } else if (Array.isArray(response)) {
        workspaces = response;
      } else {
        workspaces = [];
      }

      if (workspaces && workspaces.length > 0) {
        const transformedWorkspaces = workspaces.map(ws => {
          let wsData = {};

          if (ws.ws_data) {
            try {
              wsData = typeof ws.ws_data === 'string' ? JSON.parse(ws.ws_data) : ws.ws_data;
            } catch (e) {
              wsData = {};
            }
          }

          // Calculate indicator count
          let indicatorCount = 0;
          if (typeof wsData.indicators === 'number') {
            indicatorCount = wsData.indicators;
          } else if (Array.isArray(wsData.savedIndicators)) {
            indicatorCount = wsData.savedIndicators.length;
          } else if (wsData.indicators && Array.isArray(wsData.indicators)) {
            indicatorCount = wsData.indicators.length;
          } else if (typeof ws.indicators === 'number') {
            indicatorCount = ws.indicators;
          } else if (ws.savedIndicators && Array.isArray(ws.savedIndicators)) {
            indicatorCount = ws.savedIndicators.length;
          }

          // Calculate location count
          let locationCount = 0;
          if (typeof wsData.locations === 'number') {
            locationCount = wsData.locations;
          } else if (Array.isArray(wsData.savedLocations)) {
            locationCount = wsData.savedLocations.length;
          } else if (wsData.locations && Array.isArray(wsData.locations)) {
            locationCount = wsData.locations.length;
          } else if (typeof ws.locations === 'number') {
            locationCount = ws.locations;
          } else if (ws.savedLocations && Array.isArray(ws.savedLocations)) {
            locationCount = ws.savedLocations.length;
          }

          // Handle creation date
          let createdDate = null;
          if (ws.created_at) {
            createdDate = ws.created_at;
          } else if (ws.createdAt) {
            createdDate = ws.createdAt;
          } else if (wsData.createdAt) {
            createdDate = wsData.createdAt;
          } else {
            createdDate = new Date().toISOString();
          }

          return {
            id: ws.id,
            name: ws.ws_name || ws.name || 'Unnamed Workspace',
            indicators: indicatorCount,
            locations: locationCount,
            createdAt: createdDate,
            isDefault: ws.user_default || wsData.isDefault || false,
            rawData: wsData
          };
        });

        setWorkspaces(transformedWorkspaces);
      } else {
        setWorkspaces([]);
      }
    } catch (error) {
      let errorMessage = 'Failed to load workspaces. Please try again.';

      if (error.status === 401) {
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (error.status === 403) {
        errorMessage = 'You do not have permission to view workspaces.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message.includes('Network error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
      showAlert(errorMessage, 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Load workspaces on component mount
  useEffect(() => {
    if (isAuthenticated) {
      loadWorkspaces();
    } else {
      setWorkspaces([]);
    }
  }, [isAuthenticated]);

  // Handle toggle default workspace - FIXED: Now shows confirmation
  const handleToggleDefault = (workspaceId) => {
    const workspaceName = workspaces.find(ws => ws.id === workspaceId)?.name;

    showConfirm(
      `Are you sure you want to set "${workspaceName}" as your default workspace?`,
      async () => {
        setSettingDefault(workspaceId);

        try {
          await setDefaultWorkspace(workspaceId);

          const updatedWorkspaces = workspaces.map(workspace => ({
            ...workspace,
            isDefault: workspace.id === workspaceId
          }));

          setWorkspaces(updatedWorkspaces);
          showAlert(`"${workspaceName}" has been set as your default workspace!`, 'success');

        } catch (error) {
          let errorMessage = 'Failed to set default workspace. Please try again.';

          if (error.status === 401) {
            errorMessage = 'Your session has expired. Please log in again.';
          } else if (error.status === 404) {
            errorMessage = 'Workspace not found. It may have been deleted.';
            loadWorkspaces();
          }

          showAlert(errorMessage, 'error');
        } finally {
          setSettingDefault(null);
        }
      }
    );
  };

  // Handle load workspace - FIXED: Now shows confirmation dialog
  const handleLoad = (workspace) => {
    showConfirm(
      `Are you sure you want to load workspace "${workspace.name}"? This will replace your current workspace data.`,
      async () => {
        setIsLoading(true);


        try {
          const response = await getWorkspaceById(workspace.id);
          const fullWorkspaceData = response.data.workspace;

          const workspaceData = typeof fullWorkspaceData.ws_data === 'string'
            ? JSON.parse(fullWorkspaceData.ws_data)
            : fullWorkspaceData.ws_data;

          // Load indicators to Redux
          if (workspaceData.savedIndicators && workspaceData.savedIndicators.length > 0) {
            dispatch(setIndicators(workspaceData.savedIndicators));
          }

          // Load locations to Redux
          if (workspaceData.savedLocations && workspaceData.savedLocations.length > 0) {
            const normalizedLocations = workspaceData.savedLocations.slice(1).map(loc => ({
              id: loc.id || loc.value || loc.lgd_code || null,
              name: loc.label || loc.lgd_name || loc.name || '',
              geo_level: loc.geo_level || loc.geo_level_code || null,
              properties: {
                id: loc.id || loc.value || loc.lgd_code || null,
                code: loc.value || loc.lgd_code || null,
                name: loc.label || loc.lgd_name || loc.name || '',
                geo_level_code: loc.geo_level || loc.geo_level_code || null,
              }
            }));

            dispatch(setMapIndicators(normalizedLocations));
          }
          dispatch(clearSavedCharts([]))
          if (workspaceData.savedChart && workspaceData.savedChart?.length > 0) {
            workspaceData.savedChart.forEach(chart => {
        dispatch(saveCharts(chart));
    });
          } else {
            dispatch(clearSavedCharts([]));
          }

          dispatch(setWorkspaceLoaded({
            id: fullWorkspaceData.id,
            name: fullWorkspaceData.ws_name,
            workspace: fullWorkspaceData
          }));
          setShowWorkspace(!Prev)
          showAlert(`Workspace "${fullWorkspaceData.ws_name}" loaded successfully!`, 'success');

        } catch (error) {
          let errorMessage = 'Error loading workspace. Please try again.';

          if (error.status === 401) {
            errorMessage = 'Your session has expired. Please log in again.';
          } else if (error.status === 404) {
            errorMessage = 'Workspace not found. It may have been deleted.';
            loadWorkspaces();
          }

          showAlert(errorMessage, 'error');
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  // Handle delete workspace - FIXED: Now uses blue/white confirmation
  const handleDelete = (workspaceId) => {
    const workspaceName = workspaces.find(ws => ws.id === workspaceId)?.name;

    showConfirm(
      `Are you sure you want to delete "${workspaceName}"? This action cannot be undone.`,
      async () => {
        try {
          await deleteWorkspace(workspaceId);

          setWorkspaces(workspaces.filter(workspace => workspace.id !== workspaceId));
          showAlert('Workspace deleted successfully!', 'success');

        } catch (error) {
          let errorMessage = 'Error deleting workspace. Please try again.';

          if (error.status === 401) {
            errorMessage = 'Your session has expired. Please log in again.';
          } else if (error.status === 404) {
            errorMessage = 'Workspace not found. It may have been already deleted.';
            loadWorkspaces();
          }

          showAlert(errorMessage, 'error');
        }
      }
    );
  };

    const handleSaveSuccess = (workspaceData) => {
    alert(`Workspace "${workspaceData.name}" saved successfully!`);
    loadWorkspaces();
    setShowDetails(!showDetails);
    setShowWorkspace(!showWorkspace);

    // Always load the saved workspace as current
    if (workspaceData.id && workspaceData.name) {
      setTimeout(() => {
        dispatch(setWorkspaceLoaded({
          id: workspaceData.id,
          name: workspaceData.name,
          workspace: workspaceData
        }));
      }, 100);
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="container-fluid mt-4 px-3">
        <div className="alert alert-warning">
          <h4 className="alert-heading">Access Denied</h4>
          <p>Please log in to access your workspace.</p>
        </div>
      </div>
    );
  }
  const geoLevelLabel = (level) => {
    switch (level) {
      case 1: return "National";
      case 2: return "State / UT";
      case 3: return "District";
      case 4: return "ULG";
      default: return "Unknown";
    }
  };

  const handleRemoveChart = (chartName) => {
    dispatch(removeSavedCharts(chartName));

  };
  const handleViewChart = (chartName) => {

    const chart = savedCharts.filter(c => c.chartName === chartName)[0];
    const chartConfigMap = {
      "/temporal-barchart": {
        reduxAction: setBarConfig,
        visualizationType: { value: "bar", label: "Column/ Bar Chart" },
        extra: setUseTemproBarChart
      },
      "/barChart": {
        reduxAction: setBarConfig,
        visualizationType: { value: "bar", label: "Column/ Bar Chart" }
      },
      "/piechart": {
        reduxAction: setPieConfig,
        visualizationType: { value: "pie", label: "Pie / Doughnut Chart" }
      },
      "/stackedcolumnchart": {
        reduxAction: setStackedConfig,
        visualizationType: { value: "stacked", label: "Stacked Chart" }
      },
      "/map-visualization": {
        reduxAction: setMapConfig,
        visualizationType: { value: "map", label: "Map" }
      },
      "/ulg-map-visualization": {
        reduxAction: setUlgMapConfig,
        visualizationType: { value: "map", label: "Map" },
        extra: setUseUlgMap
      }
    };


    const chartConfig = chartConfigMap[chart.path];

    if (chartConfig?.reduxAction) {
      dispatch(chartConfig.reduxAction(chart.reduxPayload));
    }
    if (chartConfig?.extra) {
      dispatch(chartConfig.extra(true));
    }

    if (chartConfig?.visualizationType) {
      dispatch(setVisualizationType({ type: chartConfig.visualizationType.value, label: chartConfig.visualizationType.label }));
    }

    navigate(`${chart.path}`, {
      state: {
        payload: chart.payload,
        // apiResponse: res.data.indicator_values,
        formattedPayload: chart.formattedPayload,
        reduxPayload: chart.reduxPayload
      },
    });
  }

  return (
    <>
      <WorkspaceButton
        showWorkspace={showWorkspace}
        setShowWorkspace={setShowWorkspace}
      />
      <WorkspaceSidePanel showWorkspace={showWorkspace}
        setShowWorkspace={setShowWorkspace}
        onVisualize={handleVisualize}
        onSaveSuccess={handleSaveSuccess} />
      <div className="container-fluid mt-4 px-3">
        {/* Error Display */}
        {error && (
          <div className="row mb-4">
            <div className="col">
              <div className="alert alert-danger">
                {error}
              </div>
            </div>
          </div>
        )}

        {/* Workspaces Section */}
        <div className="row mb-4">
          <div className="col">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0 fw-bold">Your Workspaces ({workspaces.length})</h4>
              <button
                className="btn btn-outline-primary btn-sm d-flex align-items-center"
                onClick={loadWorkspaces}
                disabled={isRefreshing}
              >
                <LuRefreshCw size={16} className={`me-2 ${isRefreshing ? 'spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {workspaces.length === 0 ? (
          <div className="row">
            <div className="col">
              <div className="card border rounded shadow-sm">
                <div className="card-body text-center py-5">
                  <h5 className="text-muted fw-bold">No saved workspaces found</h5>
                  <p className="text-muted mb-3">
                    Go to the Map page and save your workspace to see it here.
                  </p>
                  <a href="/map" className="btn btn-primary shadow-sm">
                    Go to Map
                  </a>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            {workspaces.map(workspace => (
              <div key={workspace.id} className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4">
                <div className={`card h-100 border rounded shadow-sm ${workspace.isDefault ? 'border-primary' : ''}`}>
                  <div className="card-body d-flex flex-column p-3">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h5 className="card-title mb-0 flex-grow-1 fw-bold">
                        {workspace.name}
                      </h5>
                      <button
                        className="btn btn-sm btn-outline-danger border-0"
                        onClick={() => handleDelete(workspace.id)}
                        title="Delete Workspace"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>



                    {/* Statistics */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <MdOutlineLocalActivity size={16} className="text-primary me-2" />
                          <span className="text-muted small">Indicators</span>
                        </div>
                        <span className="badge bg-primary">{workspace.indicators || 0}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <LuMapPin size={16} className="text-primary me-2" />
                          <span className="text-muted small">Locations</span>
                        </div>
                        <span className="badge bg-primary">{workspace.locations || 0}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <LuBarChart2 size={16} className="text-primary me-2" />
                          <span className="text-muted small">Charts</span>
                        </div>
                        <span className="badge bg-primary">{workspace.rawData?.savedChart?.length || 0}</span>
                      </div>
                    </div>

                    {/* Created date */}
                    <div className="mb-3">
                      <small className="text-muted">
                        Created: {workspace.createdAt ? new Date(workspace.createdAt).toLocaleDateString() : 'N/A'}
                      </small>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-auto">
                      <div className="d-flex gap-2 mb-2">
                        <button
                          className="btn btn-primary flex-fill shadow-sm"
                          onClick={() => handleLoad(workspace)}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Loading...
                            </>
                          ) : (
                            'Load'
                          )}
                        </button>

                        {workspace.isDefault ? (
                          <div className="btn btn-primary flex-fill" style={{ pointerEvents: 'none', cursor: 'default' }}>
                            Default
                          </div>
                        ) : (
                          <button
                            className="btn btn-outline-secondary flex-fill"
                            onClick={() => handleToggleDefault(workspace.id)}
                            disabled={settingDefault === workspace.id}
                          >
                            {settingDefault === workspace.id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Setting...
                              </>
                            ) : (
                              'Set Default'
                            )}
                          </button>
                        )}
                      </div>

                      {/* View Details Button */}
                      {
                        !(hasUnsavedChanges && loadedWorkspaceName === workspace.name)  && (
                          <div className="d-flex">
                          <button
                            className={`btn flex-fill ${hasUnsavedChanges && loadedWorkspaceName === workspace.name ? 'btn-info' : 'btn-muted'}`}
                            onClick={() => handleViewDetails(workspace)}
                            disabled={hasUnsavedChanges && loadedWorkspaceName === workspace.name}
                          >
                            View Details
                          </button>
                        </div>
                      )}
                    </div>
                    {hasUnsavedChanges && loadedWorkspaceName === workspace.name && (
                      <div>
                        <p className="mt-2 fs--1"><b><i className="fw-bold">Note:</i></b> This workspace is currently used workspace and is modified. Save the workspace to view the details. </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Workspace Details Section */}
        {showDetails && selectedWorkspaceDetails && (
          <div id="workspace-details-section" className="row mt-4">
            <div className="col">
              <div className="card border rounded shadow-sm">
                <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold">
                    <CiCircleInfo className="me-2" />
                    Workspace Details: {selectedWorkspaceDetails.name}
                  </h5>
                  <button
                    className="btn btn-sm btn-outline-secondary border-0"
                    onClick={handleCloseDetails}
                  >
                    <RxCross2 size={16} />
                  </button>
                </div>
                <div className="card-body p-3">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-primary fw-bold mb-3">Basic Information</h6>
                      <div className="list-group list-group-flush">
                        <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <span className="fw-bold">Workspace Name:</span>
                          <span>{selectedWorkspaceDetails.name}</span>
                        </div>
                        <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <span className="fw-bold">Created:</span>
                          <span>{selectedWorkspaceDetails.createdAt ? new Date(selectedWorkspaceDetails.createdAt).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <span className="fw-bold">Status:</span>
                          {selectedWorkspaceDetails.isDefault ? (
                            <span className="badge bg-primary">Default Workspace</span>
                          ) : (
                            <span className="badge bg-secondary">Regular Workspace</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-primary fw-bold mb-3">Content Summary</h6>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <MdOutlineLocalActivity size={16} className="text-primary me-2" />
                          <span className="fw-bold">Indicators:</span>
                        </div>
                        <span className="badge bg-primary">{selectedWorkspaceDetails.indicators || 0}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <LuMapPin size={16} className="text-primary me-2" />
                          <span className="fw-bold">Locations:</span>
                        </div>
                        <span className="badge bg-primary">{selectedWorkspaceDetails.locations || 0}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <LuBarChart2 size={16} className="text-primary me-2" />
                          <span className="fw-bold">Charts:</span>
                        </div>
                        <span className="badge bg-primary">{selectedWorkspaceDetails.rawData.savedChart?.length || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Structured Data Section */}
                  {selectedWorkspaceDetails.rawData && (
                    <div className="mt-3">
                      <h6 className="text-primary">Workspace Content Details</h6>

                      {selectedWorkspaceDetails.rawData.savedChart
                        && selectedWorkspaceDetails.rawData.savedChart?.length > 0 && (
                          <div className="mb-4">
                            <h6 className="text-primary mb-3 fw-bold">
                              <BiBarChartSquare className="me-2" />
                              Charts ({selectedWorkspaceDetails.rawData?.savedChart?.length})
                            </h6>
                            <div className="list-group border rounded shadow-sm" style={{ maxHeight: '300px', overflow: 'auto' }}>
                              {selectedWorkspaceDetails.rawData?.savedChart.map((chart, index) => (
                                <div key={index} className="list-group-item">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                      <div className="mb-1 d-flex justify-content-between align-items-center">
                                        <span className="text-dark fw-bold">
                                          {chart.chartName}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Indicators Section */}
                      {selectedWorkspaceDetails.rawData.savedIndicators && selectedWorkspaceDetails.rawData.savedIndicators.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-primary mb-3 fw-bold">
                            <MdOutlineLocalActivity className="me-2" />
                            Indicators ({selectedWorkspaceDetails.rawData.savedIndicators.length})
                          </h6>
                          <div className="list-group border rounded shadow-sm" style={{ maxHeight: '300px', overflow: 'auto' }}>
                            {selectedWorkspaceDetails.rawData.savedIndicators.map((indicator, index) => (
                              <div key={index} className="list-group-item">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <div className="mb-1">
                                      <span className="text-dark fw-bold">
                                        {[
                                          indicator.indicator_name
                                            ? `${indicator.indicator_name} (${indicator.dataset_name || ""})`
                                            : "",
                                          indicator.indicator_date || "",
                                          indicator.geo_level_string || "",
                                          indicator.agency_name || "",
                                        ]
                                          .filter((val) => val && val !== "NA")
                                          .join(" | ")}
                                      </span>
                                    </div>
                                    <div className="mt-1">
                                      <small className="text-muted">
                                        {[indicator.sector_name, indicator.sub_sector_name, indicator.sub_sub_sector_name]
                                          .filter((val) => val && val !== "NA")
                                          .join(" | ")}
                                      </small>
                                    </div>
                                  </div>
                                </div>

                                {/* Sub-indicators if available
                                {indicator.sub_indicators && indicator.sub_indicators.length > 0 && (
                                  <div className="mt-2">
                                    {indicator.sub_indicators.map((subIndicator, subIndex) => (
                                      <div key={subIndex} className="mb-1 p-2 bg-light rounded">
                                        <small className="text-dark">
                                          {subIndicator.name || subIndicator.sub_indicator || `Sub-indicator ${subIndex + 1}`}
                                        </small>
                                      </div>
                                    ))}
                                  </div>
                                )} */}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Locations Section */}
                      {selectedWorkspaceDetails.rawData.savedLocations && selectedWorkspaceDetails.rawData.savedLocations.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-primary mb-3 fw-bold">
                            <LuMapPin className="me-2" />
                            Locations ({selectedWorkspaceDetails.rawData.savedLocations.length})
                          </h6>
                          <div className="list-group border rounded shadow-sm" style={{ maxHeight: '300px', overflow: 'auto' }}>
                            {selectedWorkspaceDetails.rawData.savedLocations.map((location, index) => (
                              <div key={index} className="list-group-item d-flex align-items-center">
                                <LuMapPin size={16} className="text-primary me-2" />
                                <span className="text-dark fw-bold">
                                  {location.name || location.label || location.lgd_name || `Location ${index + 1}`}
                                </span>

                                <span className="small text-secondary ms-2">({geoLevelLabel(location.geo_level || 4)})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show raw JSON as fallback if no structured data */}
                      {(!selectedWorkspaceDetails.rawData.savedIndicators || selectedWorkspaceDetails.rawData.savedIndicators.length === 0) &&
                        (!selectedWorkspaceDetails.rawData.savedLocations || selectedWorkspaceDetails.rawData.savedLocations.length === 0) && (
                          <div className="mt-3">
                            <h6 className="text-primary">Raw Workspace Data</h6>
                            <div className="bg-light p-3 rounded">
                              <pre className="mb-0" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                {JSON.stringify(selectedWorkspaceDetails.rawData, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-3 d-flex gap-2">
                    <button
                      className="btn btn-primary shadow-sm"
                      onClick={() => {
                        handleCloseDetails();
                        handleLoad(selectedWorkspaceDetails);
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Loading...
                        </>
                      ) : (
                        'Load This Workspace'
                      )}
                    </button>

                    {!selectedWorkspaceDetails.isDefault && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          handleCloseDetails();
                          handleToggleDefault(selectedWorkspaceDetails.id);
                        }}
                        disabled={settingDefault === selectedWorkspaceDetails.id}
                      >
                        {settingDefault === selectedWorkspaceDetails.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Setting...
                          </>
                        ) : (
                          'Set as Default'
                        )}
                      </button>
                    )}

                    <button
                      className="btn btn-outline-danger"
                      onClick={() => {
                        handleCloseDetails();
                        handleDelete(selectedWorkspaceDetails.id);
                      }}
                    >
                      Delete Workspace
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alert positioned at top (like your CustomToast) */}
        {alertinfo && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000
          }}>
            <CustomToast
              message={alertinfo.message}
              type={alertinfo.type}
              onClose={() => setAlertinfo(null)}
            />
          </div>
        )}

        {/* Confirm Dialog - FIXED: Now properly renders */}
        {confirm && (
          <CustomConfirm
            message={confirm.message}
            onConfirm={confirm.onConfirm}
            onCancel={confirm.onCancel}
          />
        )}
      </div>

      <style jsx>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes slideInRight {
          from { 
            opacity: 0; 
            transform: translateX(100%); 
          }
          to { 
            opacity: 1; 
            transform: translateX(0); 
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.8); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
      `}</style>
    </>
  );
};

export default Space;